import { useQuery } from 'react-query';
import type { UseQueryResult } from 'react-query';

import { getProductCatalog as getLegacyProductCatalog } from '@hotjar/webapp/domains/common/product/api';
import type { ProductCatalog } from '@hotjar/webapp/domains/common/product/types';
import { useCurrentSite } from '@hotjar/site';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { useProductCatalog as useProductCatalogV3 } from '@hotjar/product-catalog';
import { useSubscriptionInfo } from '@hotjar/subscription-management';
import { mapV3ProductCatalogToLegacy } from '../mapper';

const PRODUCT_CATALOG_QUERY_KEY = 'product_catalog';
const CACHE_TIME = 1000 * 60 * 60 * 24; // 24h
const DEFAULT_BILLING_PERIOD = 'MONTHLY';
const DEFAULT_CURRENCY = 'EUR';

interface UseProductCatalogProps {
  enabled?: boolean;
}

/**
 * @deprecated
 * Use billing v3.0 `useProductCatalog` from '@hotjar/product-catalog' instead
 *
 * import { useProductCatalog } from '@hotjar/product-catalog';
 *
 */
export const useProductCatalog = (
  props?: UseProductCatalogProps,
): UseQueryResult<ProductCatalog, Error> => {
  const { enabled = true } = props || {};
  const { data: currentSite, isLoading: isLoadingSite } = useCurrentSite();

  const isBillingV3 = isBillingV3Enabled(currentSite?.account);
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscriptionInfo({
    enabled: isBillingV3,
  });

  const { customer } = subscription || {};

  const { data: productCatalogV3, isLoading: isCatalogLoading } = useProductCatalogV3({
    billingPeriod: DEFAULT_BILLING_PERIOD,
    currency: customer?.currency ?? DEFAULT_CURRENCY,
    enabled: isBillingV3,
  });

  return useQuery(
    PRODUCT_CATALOG_QUERY_KEY,
    async () => {
      if (!isBillingV3) {
        return getLegacyProductCatalog();
      }
      return productCatalogV3 && mapV3ProductCatalogToLegacy(productCatalogV3);
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
      enabled:
        enabled &&
        !isLoadingSite &&
        !isLoadingSubscription &&
        !isCatalogLoading &&
        Boolean(currentSite?.account),
    },
  );
};
